import React from "react"
import type { CSSProperties } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import cx from "classnames"

/* TODO: Make svg icon for all other social icons and add them to the svgIcons array */
import { icons, svgIcons } from "./icons"
import "./library"

export const lookupSocialIcon = (icon: string) =>
  icons.find(i => i.iconName === icon)

const FontAwesomeNameFallbacks = [
  { old: "external-link", new: "arrow-up-right-from-square" },
  { old: "shopping-cart", new: "cart-shopping" },
  { old: "telegram-plane", new: "telegram" },
  { old: "snapchat-ghost", new: "snapchat" }
]

const SIZES: Record<string, string> = {
  sm: "size-4",
  md: "size-6",
  lg: "size-8"
}

export type SocialIconProps = {
  /** Icon name matching a FontAwesome or custom SVG icon (e.g. "twitter", "instagram", "cart-shopping") */
  name?: string
  /** Optional size to match the Icon component */
  size?: "sm" | "md" | "lg"
  /** Custom inline styles applied to the icon */
  style?: CSSProperties
  /** Additional CSS class names */
  className?: string
}

/**
 * Renders a social media icon by name, using FontAwesome icons with fallback
 * support for renamed icons and custom SVG replacements.
 */
export default function SocialIcon({
  name,
  size,
  style,
  className
}: SocialIconProps) {
  const sizeClass = size ? SIZES[size] : null
  const svgIcon = svgIcons.find(i => i.name === name)
  if (svgIcon) {
    const IconComponent = svgIcon.component
    return (
      <IconComponent
        className={cx(className, "inline-block", sizeClass || "w-3")}
        style={{ fontSize: "inherit", ...style }}
      />
    )
  }
  let iconName
  if (name && typeof name == "string") {
    const oldName = FontAwesomeNameFallbacks.find(f => f.old === name)
    if (oldName) {
      iconName = lookupSocialIcon(oldName.new)
    } else {
      iconName = lookupSocialIcon(name)
    }
  }

  return (
    <FontAwesomeIcon
      icon={iconName as IconProp}
      className={cx({ "inline-block": size }, sizeClass, className)}
      style={style}
    />
  )
}

export { SocialIcon }
